import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUpsertListing } from '@/hooks/use-upsert-listing';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import type { StoreListing, StorePlatform, UpsertListingBody } from '@/types/store';

import {
  type ListingFieldName,
  PLATFORM_FIELD_SPECS,
  PLATFORM_LABELS,
} from './store-constants';

interface ListingDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: StorePlatform;
  locale: string;
  /** Existing row when editing; undefined when creating a new draft. */
  listing?: StoreListing;
}

type FieldValues = Partial<Record<ListingFieldName, string>>;

/** Accepts only http(s) URLs — mirrors the backend `@IsUrl()` guard. */
function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value);

    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function initialValues(platform: StorePlatform, listing?: StoreListing): FieldValues {
  const values: FieldValues = {};

  for (const spec of PLATFORM_FIELD_SPECS[platform]) {
    values[spec.name] = listing?.[spec.name] ?? '';
  }

  return values;
}

/**
 * Right-side editor for one platform+locale listing. Saving persists a **draft**
 * (`PUT`, partial merge). Text fields are written as typed (blank clears them);
 * URL fields are omitted when blank so the server's `@IsUrl()` never sees `""`,
 * and are validated client-side before submit.
 */
export function ListingDrawer({
  open,
  onOpenChange,
  platform,
  locale,
  listing,
}: ListingDrawerProps): JSX.Element {
  const specs = PLATFORM_FIELD_SPECS[platform];
  const [values, setValues] = useState<FieldValues>(() => initialValues(platform, listing));
  const [errors, setErrors] = useState<FieldValues>({});
  const upsert = useUpsertListing();

  useEffect(() => {
    if (open) {
      setValues(initialValues(platform, listing));
      setErrors({});
    }
  }, [open, platform, listing]);

  function setField(name: ListingFieldName, value: string): void {
    setValues((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: undefined }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    const body: UpsertListingBody = {};
    const nextErrors: FieldValues = {};

    for (const spec of specs) {
      const raw = values[spec.name] ?? '';

      if (spec.kind === 'url') {
        const trimmed = raw.trim();

        if (!trimmed) continue; // omit empty URLs — @IsUrl() rejects ""
        if (!isValidUrl(trimmed)) {
          nextErrors[spec.name] = 'Enter a valid http(s) URL.';
          continue;
        }
        body[spec.name] = trimmed;
      } else {
        // Text fields are WYSIWYG: send exactly what's shown ("" clears).
        body[spec.name] = raw;
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);

      return;
    }

    upsert.mutate(
      { platform, locale, body },
      {
        onSuccess: () => {
          toast.success(`Saved ${PLATFORM_LABELS[platform]} · ${locale} draft.`);
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : 'Could not save the draft.');
        },
      },
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="sm:max-w-xl">
        <DrawerHeader>
          <DrawerTitle>
            {listing ? 'Edit' : 'New'} {PLATFORM_LABELS[platform]} listing
          </DrawerTitle>
          <DrawerDescription>
            Locale <span className="font-mono text-foreground">{locale}</span>. Saving stores a
            draft — publish it from the table to push it live.
          </DrawerDescription>
        </DrawerHeader>

        <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-1 flex-col gap-4">
          {/* -mx-2/px-2 gives the focus ring room so overflow-y-auto doesn't clip
              it at the edges, while keeping the fields aligned with header/footer. */}
          <div className="-mx-2 min-h-0 flex-1 space-y-4 overflow-y-auto px-2 py-1">
            {specs.map((spec) => {
              const value = values[spec.name] ?? '';
              const error = errors[spec.name];
              const errorId = `${spec.name}-error`;
              const helpId = `${spec.name}-help`;
              const noteId = `${spec.name}-note`;
              // A URL field blanked after having had a value: the store requires a
              // valid URL (@IsUrl rejects ""), so clearing it here is a no-op — say so.
              const urlCleared =
                spec.kind === 'url' &&
                value.trim() === '' &&
                (listing?.[spec.name] ?? '').trim() !== '';
              const showHelp = Boolean(spec.help) && !error;
              const showNote = urlCleared && !error;
              const describedBy =
                [error ? errorId : null, showHelp ? helpId : null, showNote ? noteId : null]
                  .filter(Boolean)
                  .join(' ') || undefined;

              return (
                <div key={spec.name} className="space-y-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <Label htmlFor={`listing-${spec.name}`}>{spec.label}</Label>
                    {spec.maxLength && (
                      <span
                        className={cn(
                          'font-mono text-[10px] tabular-nums',
                          value.length > spec.maxLength
                            ? 'text-status-failed'
                            : 'text-muted-foreground',
                        )}
                      >
                        {value.length}/{spec.maxLength}
                      </span>
                    )}
                  </div>

                  {spec.kind === 'textarea' ? (
                    <Textarea
                      id={`listing-${spec.name}`}
                      rows={spec.name === 'fullDescription' ? 8 : 3}
                      maxLength={spec.maxLength}
                      value={value}
                      placeholder={spec.placeholder}
                      aria-invalid={Boolean(error)}
                      aria-describedby={describedBy}
                      onChange={(e) => setField(spec.name, e.target.value)}
                    />
                  ) : (
                    <Input
                      id={`listing-${spec.name}`}
                      type={spec.kind === 'url' ? 'url' : 'text'}
                      maxLength={spec.maxLength}
                      value={value}
                      placeholder={spec.placeholder}
                      aria-invalid={Boolean(error)}
                      aria-describedby={describedBy}
                      onChange={(e) => setField(spec.name, e.target.value)}
                    />
                  )}

                  {showHelp && (
                    <p id={helpId} className="text-[11px] text-muted-foreground">
                      {spec.help}
                    </p>
                  )}
                  {showNote && (
                    <p id={noteId} className="text-[11px] text-status-degraded">
                      The store requires a valid URL, so this can’t be cleared here — it keeps its
                      current value.
                    </p>
                  )}
                  {error && (
                    <p id={errorId} className="text-xs text-destructive" role="alert">
                      {error}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save draft'}
            </Button>
          </div>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
